import { useEffect, useState } from "react";

import { toastMessageActions } from "../state/request";
import { useDispatch } from "react-redux";

const OFFLINE_DEBOUNCE_MS = 2000;
const RESUME_GRACE_MS = 3000;

let lastResumeAt = 0;

export const isWithinResumeGrace = (): boolean =>
  lastResumeAt > 0 && Date.now() - lastResumeAt < RESUME_GRACE_MS;

const useNetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const dispatch = useDispatch();

  useEffect(() => {
    let pendingOfflineTimer: ReturnType<typeof setTimeout> | null = null;

    const clearPendingOfflineTimer = () => {
      if (pendingOfflineTimer) {
        clearTimeout(pendingOfflineTimer);
        pendingOfflineTimer = null;
      }
    };

    const handleOnline = () => {
      clearPendingOfflineTimer();
      setIsOnline(true);
      dispatch(toastMessageActions.resetGlobalAlert());
    };

    const handleOffline = () => {
      clearPendingOfflineTimer();
      pendingOfflineTimer = setTimeout(() => {
        pendingOfflineTimer = null;

        if (navigator.onLine || isWithinResumeGrace()) return;
        setIsOnline(false);
        dispatch(
          toastMessageActions.setGlobalAlert({
            severity: "error",
            message:
              "Network connection lost. Please check your internet connection.",
          }),
        );
      }, OFFLINE_DEBOUNCE_MS);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        lastResumeAt = Date.now();

        clearPendingOfflineTimer();
      }
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      document.removeEventListener(
        "visibilitychange", handleVisibilityChange,
      );
      clearPendingOfflineTimer();
    };
  }, [dispatch]);

  return isOnline;
};

export default useNetworkStatus;
